import type { Session } from "@supabase/supabase-js";
import { appConfig, supabase } from "./config";
import type {
  AdminOverview,
  AvailabilitySlot,
  BookingRequestInput,
  ChallengeInput,
  PublicChallenge,
  PublicChallengeState,
} from "../types";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code = "unknown",
    public readonly status = 500,
  ) {
    super(message);
  }
}

async function invoke<T>(
  functionName: string,
  options: { method?: "GET" | "POST"; body?: unknown; session?: Session | null } = {},
): Promise<T> {
  if (!appConfig.isConfigured) {
    throw new ApiError("Die Anwendung ist noch nicht mit Supabase verbunden.", "not_configured", 503);
  }

  const response = await fetch(`${appConfig.supabaseUrl}/functions/v1/${functionName}`, {
    method: options.method ?? "POST",
    headers: {
      apikey: appConfig.supabaseAnonKey,
      Authorization: `Bearer ${options.session?.access_token ?? appConfig.supabaseAnonKey}`,
      "Content-Type": "application/json",
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  } & T;

  if (!response.ok) {
    throw new ApiError(
      payload.error ?? "Die Anfrage konnte nicht verarbeitet werden.",
      payload.code ?? "request_failed",
      response.status,
    );
  }

  return payload;
}

function mockAvailability(): AvailabilitySlot[] {
  const slots: AvailabilitySlot[] = [];
  const cursor = new Date();
  cursor.setDate(cursor.getDate() + 2);
  cursor.setHours(15, 0, 0, 0);

  while (slots.length < 8) {
    const weekday = cursor.getDay();
    if (weekday !== 0 && weekday !== 6) {
      for (const hour of [15, 16]) {
        const start = new Date(cursor);
        start.setHours(hour, 0, 0, 0);
        const end = new Date(start.getTime() + 45 * 60_000);
        const requests = slots.length % 4;
        slots.push({
          start: start.toISOString(),
          end: end.toISOString(),
          requests,
          remaining: 3 - requests,
        });
        if (slots.length >= 8) break;
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return slots;
}

export const api = {
  async availability(): Promise<AvailabilitySlot[]> {
    if (appConfig.useMockApi) return mockAvailability();
    const result = await invoke<{ slots: AvailabilitySlot[] }>("availability", { method: "GET" });
    return result.slots;
  },

  async createBookingRequest(input: BookingRequestInput) {
    if (appConfig.useMockApi) {
      return { id: crypto.randomUUID(), status: "pending" as const };
    }
    return invoke<{ id: string; status: "pending" }>("booking-request", { body: input });
  },

  async createBookingRequestWithAttachment(input: BookingRequestInput, attachment: File) {
    if (appConfig.useMockApi) return { id: crypto.randomUUID(), status: "pending" as const };
    if (!appConfig.isConfigured) throw new ApiError("Die Anwendung ist noch nicht mit Supabase verbunden.", "not_configured", 503);
    const form = new FormData();
    Object.entries(input).forEach(([key, value]) => {
      if (value !== undefined) form.set(key, String(value));
    });
    form.set("attachment", attachment, attachment.name);
    const response = await fetch(`${appConfig.supabaseUrl}/functions/v1/booking-request`, {
      method: "POST",
      headers: {
        apikey: appConfig.supabaseAnonKey,
        Authorization: `Bearer ${appConfig.supabaseAnonKey}`,
      },
      body: form,
    });
    const payload = await response.json().catch(() => ({})) as { error?: string; code?: string; id?: string; status?: "pending" };
    if (!response.ok) throw new ApiError(payload.error ?? "Die Anfrage konnte nicht verarbeitet werden.", payload.code ?? "request_failed", response.status);
    return { id: payload.id ?? "", status: payload.status ?? "pending" as const };
  },

  async prepareChallenge(input: ChallengeInput, file: File) {
    if (appConfig.useMockApi) {
      return {
        submissionId: crypto.randomUUID(),
        challengeCode: `CH-${new Date().getFullYear()}-DEMO`,
        path: "mock/file",
        token: "mock",
      };
    }
    return invoke<{
      submissionId: string;
      challengeCode: string;
      path: string;
      token: string;
    }>("challenge-submit", {
      body: {
        action: "prepare",
        input,
        file: { name: file.name, type: file.type, size: file.size },
      },
    });
  },

  async uploadChallengeFile(path: string, token: string, file: File) {
    if (appConfig.useMockApi) return;
    if (!supabase) throw new ApiError("Supabase ist nicht konfiguriert.", "not_configured", 503);
    const { error } = await supabase.storage
      .from("challenge-private")
      .uploadToSignedUrl(path, token, file, { contentType: file.type });
    if (error) throw new ApiError(error.message, "upload_failed", 400);
  },

  async finalizeChallenge(submissionId: string) {
    if (appConfig.useMockApi) return { id: submissionId, status: "new" as const };
    return invoke<{ id: string; status: "new" }>("challenge-submit", {
      body: { action: "finalize", submissionId },
    });
  },

  async publicChallenges(): Promise<PublicChallengeState> {
    if (appConfig.useMockApi) return { enabled: true, challenges: [] };
    if (!appConfig.isConfigured) return { enabled: false, challenges: [] };
    return invoke<PublicChallengeState>("public-challenges", { method: "GET" });
  },

  async adminOverview(session: Session): Promise<AdminOverview> {
    return invoke<AdminOverview>("admin", {
      body: { action: "overview" },
      session,
    });
  },

  async adminAction<T>(session: Session, action: string, data: Record<string, unknown> = {}) {
    return invoke<T>("admin", { body: { action, ...data }, session });
  },

  async uploadPublicPreview(path: string, token: string, file: File) {
    if (!supabase) throw new ApiError("Supabase ist nicht konfiguriert.", "not_configured", 503);
    const { error } = await supabase.storage
      .from("challenge-public")
      .uploadToSignedUrl(path, token, file, { contentType: file.type });
    if (error) throw new ApiError(error.message, "upload_failed", 400);
  },
};
